import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analyticsEvents';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { clearCart } = useCart();

  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const hasConfirmedRef = useRef(false);

  useEffect(() => {
    const confirmOrder = async () => {
      if (!sessionId) {
        setErrorMessage('Session de paiement introuvable.');
        setStatus('error');
        return;
      }

      if (hasConfirmedRef.current) return;
      hasConfirmedRef.current = true;

      try {
        const { data, error } = await supabase.functions.invoke('confirm-checkout-session', {
          body: { session_id: sessionId },
        });

        if (error) {
          throw new Error(error.message || 'Erreur lors de la confirmation du paiement.');
        }

        if (!data?.success) {
          throw new Error(data?.message || 'Le paiement n’a pas pu être confirmé.');
        }

        const order = data.order;
        const items = data.items || [];

        trackEvent('purchase', {
          transaction_id: String(order.id),
          currency: 'CHF',
          value: Number(order.total_amount || 0),
          items: items.map((item) => ({
            item_id: String(item.product_id),
            item_name: item.product_name,
            item_brand: "Léger's Coffee",
            price: Number(item.unit_price || 0),
            quantity: Number(item.quantity || 1),
          })),
        });

        clearCart();
        setStatus('success');
      } catch (err) {
        console.error('Erreur confirmation paiement:', err);
        setErrorMessage(
          err?.message || 'Nous n’avons pas pu confirmer votre paiement.'
        );
        setStatus('error');
      }
    };

    confirmOrder();
  }, [sessionId, clearCart]);

  if (status === 'loading') {
    return (
      <main
        data-testid="payment-success-page"
        className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center"
      >
        <div className="text-center">
          <div className="spinner w-12 h-12 mx-auto mb-4" />
          <p className="text-soft-charcoal-secondary">Vérification du paiement...</p>
        </div>
      </main>
    );
  }

  if (status === 'error') {
    return (
      <main
        data-testid="payment-error-page"
        className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center py-16"
      >
        <div className="max-w-md text-center px-6">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <Package className="w-10 h-10 text-red-500" />
          </div>

          <h1 className="font-serif text-2xl text-espresso font-semibold mb-4">
            Une erreur est survenue
          </h1>

          <p className="text-soft-charcoal-secondary mb-3">
            Nous n&apos;avons pas pu vérifier votre paiement.
          </p>

          {errorMessage && (
            <p className="text-sm text-soft-charcoal-secondary mb-8">
              {errorMessage}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="btn-primary">
              <Link to="/panier">Retour au Panier</Link>
            </Button>
            <Button asChild className="btn-secondary">
              <Link to="/contact">Nous Contacter</Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="payment-success-page"
      className="min-h-screen pt-20 bg-warm-alabaster flex items-center justify-center py-16"
    >
      <div className="max-w-lg text-center px-6">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center animate-fade-in">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>

        <h1 className="font-serif text-3xl text-espresso font-semibold mb-4 animate-fade-in animate-stagger-1">
          Merci pour votre commande !
        </h1>

        <p className="text-soft-charcoal-secondary mb-8 animate-fade-in animate-stagger-2">
          Votre paiement a été effectué avec succès.
          Vous recevrez un email de confirmation avec les détails de votre commande.
        </p>

        <div className="bg-warm-secondary p-6 mb-8 animate-fade-in animate-stagger-3">
          <h2 className="font-serif text-lg font-semibold text-espresso mb-4">
            Informations de Retrait
          </h2>
          <div className="text-soft-charcoal-secondary text-sm space-y-2">
            <p>
              <strong>Adresse :</strong><br />
              Pl. des Augustins 19, 1205 Genève
            </p>
            <p>
              <strong>Horaires :</strong><br />
              Lun-Jeu : 07:00-20:30 | Ven : 07:00-17:00 | Sam : 07:00-14:00
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animate-stagger-4">
          <Button asChild className="btn-primary">
            <Link to="/mon-compte">
              Voir mes Commandes
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
          <Button asChild className="btn-secondary">
            <Link to="/menu">Continuer mes Achats</Link>
          </Button>
        </div>
      </div>
    </main>
  );
}